/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  isAndroid,
  isIos,
  isSafari,
} from '../../Helpers/browserAndDeviceDetection';
import useFirstInteraction from '../../Hooks/useFirstInteraction/useFirstInteraction';
import {
  setIsAndroid,
  setIsIos,
  setIsSafari,
} from '../../Lib/redux/slices/formConfig';
import { updateHasSubmissionPageExitAnimationCompleted } from '../../Lib/redux/slices/transition';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';
import BrowserAutoFiller from '../BrowserAutoFiller/BrowserAutoFiller';
import CountdownTimer from '../CountdownTimer/CountdownTimer';
import { DomainWhitelistStatus } from '../DomainWhitelistStatus';
import DynamicWidgetHeight from '../DynamicWidgetHeight/DynamicWidgetHeight';
import FormHeaderSection from '../FormHeaderSection/FormHeaderSection';
import LegalStatement from '../LegalStatement/LegalStatement';
import SuppliersListModal from '../LegalStatement/SuppliersListModal';
import LogRocketSession from '../LogRocketSession/LogRocketSession';
import NavigationSection from '../NavigationSection/NavigationSection';
import Page from '../Page/Page';
import PrivacyPolicyLink from '../PrivacyPolicyLink/PrivacyPolicyLink';
import Progress from '../Progress/Progress';
import {
  activateCustomExitAnimationOfFormContainer,
  resetFormTransitionContainerStyles,
} from '../Transition/transition.helpers';
import makePageLayoutStyling from './makePageLayoutStyling';
import useIsForwardNavigationBlockedBySupplierCard from '../../Hooks/useIsForwardNavigationBlockedBySupplierCard/useIsForwardNavigationBlockedBySupplierCard';
import {
  exposeEventToPartner,
  toCustomerBrandMatchesEventBody,
} from '../../Helpers/events';
import useSupplierMatch from '../../Hooks/useSupplierMatch/useSupplierMatch';

const ThankYouPage = dynamic(
  () =>
    import(
      '../ThankYouPage/ThankYouPage' /* webpackChunkName: 'ThankYouPage' */
    ).catch((err) => {
      // eslint-disable-next-line no-console
      console.log('MVF App Warning - ThankYouPage failed to load: ', err);
      return () => null;
    }),
  {
    ssr: false,
  }
);

/* Performance Impact:
 * The benefit of this component is to shield <Form /> and its other child
 * components from unnecessary re-runs whenever this self-managed component
 * re-renders. It is considered self-managed because all of its data dependencies
 * originate from the store and not from a parent component.
 *
 * Responsibility:
 * This component manages the layout and all other aspects of the widget's pages
 * (including form pages and the thank you page).
 */
const PageLayout = () => {
  const dispatch = useDispatch();
  const {
    shouldRenderForm,
    privacyPolicy,
    countdownTimerSettings: {
      isCountdownTimerEnabled: isCountdownTimerEnabledFromFormConfig,
    },
  } = useSelector((store) => store.formConfig);
  const {
    isConsentStatementAboveNavigation:
      isConsentStatementAboveNavigationFormConfig,
    isDynamicHeightEnabled,
    widgetResizeDuration,
    iFrameId,
  } = useSelector((store) => store.optionalFormConfig);
  const submission = useSelector((store) => store.submission);
  const {
    requiresLegalStatementAboveNav:
      supplierMatchesRequireLegalStatementAboveNav,
  } = useSelector((store) => store.supplierMatches);
  const isConsentStatementAboveNavigation =
    isConsentStatementAboveNavigationFormConfig ||
    supplierMatchesRequireLegalStatementAboveNav;
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );
  const {
    hasSubmissionPageExitAnimationCompleted,
    isTransitionAnimationEnabledForSubmission,
  } = useSelector((store) => store.transition);
  const { showCpcRecommendations } = useSelector((store) => store.cpcCard);
  const headerRef = useRef();
  const footerRef = useRef();
  const { matchedAndFilteredBrands } = useSupplierMatch();
  const { isForwardNavigationBlockedBySupplierCard } =
    useIsForwardNavigationBlockedBySupplierCard();
  const isForwardNavigationBlocked =
    showCpcRecommendations || isForwardNavigationBlockedBySupplierCard;
  const isPreSubmissionPage = submission.submissionId === undefined;
  const isCountdownTimerEnabled =
    isCountdownTimerEnabledFromFormConfig &&
    !isForwardNavigationBlocked &&
    isPreSubmissionPage;

  const theme = useTheme();
  const styling = makePageLayoutStyling({
    theme,
    isCountdownTimerEnabled,
    isPrivacyPolicyEnabled: !!privacyPolicy.link,
  });

  // To enable a variable widget height that dynamically changes across pages,
  // each page needs to expose its required height to fit all elements (including
  // margin onto their page area)
  useEffect(() => {
    if (
      isDynamicHeightEnabled &&
      headerRef?.current &&
      footerRef?.current &&
      !window.__private__?.dynamicWidgetHeightData?.headerRef?.current &&
      !window.__private__?.dynamicWidgetHeightData.thankYouPageContentRef
        ?.current &&
      !window.__private__?.dynamicWidgetHeightData.newsletterSignupContainerRef
        ?.current
    ) {
      updateAndExposeDynamicHeightData({
        headerRef,
        footerRef,
      });
    }
  });

  const scrollDisabled = isDynamicHeightEnabled === true;
  const overflowStyleProp = scrollDisabled
    ? { style: { overflow: 'hidden' } }
    : {};

  const handleEnterPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };
  const { exposeInitialWidgetInteractionEvent } = useFirstInteraction();

  /**
   * useEffect
   * This is responsible for triggering the custom exit animation of the submission page.
   */
  useEffect(() => {
    if (isTransitionAnimationEnabledForSubmission) {
      const { id, token, errorMessage } = submission;
      if (id && token && errorMessage === undefined) {
        // Submission completed successfully - activateCustomExitAnimationOfFormContainer
        const animationCompletedCallback = () => {
          dispatch(
            updateHasSubmissionPageExitAnimationCompleted({
              hasSubmissionPageExitAnimationCompleted: true,
            })
          );
        };
        if (!hasSubmissionPageExitAnimationCompleted) {
          // This exit animation will only happen once per form session
          activateCustomExitAnimationOfFormContainer({
            widgetResizeDuration,
            theme,
            animationCompletedCallback,
            isBounceNavigationAnimationEnabled,
          });
        }
      }
      if (errorMessage && id === undefined && token === undefined) {
        // Submission failed - Reset style changes from the prepareFormTransitionContainerForCustomExitAnimation() call
        if (isTransitionAnimationEnabledForSubmission) {
          resetFormTransitionContainerStyles({
            isBounceNavigationAnimationEnabled,
          });
        }
      }
    }
  }, [submission, isTransitionAnimationEnabledForSubmission]);

  useEffect(() => {
    if (hasSubmissionPageExitAnimationCompleted) {
      resetFormTransitionContainerStyles({
        isBounceNavigationAnimationEnabled,
      });
    }
  }, [hasSubmissionPageExitAnimationCompleted]);

  useEffect(() => {
    batch(() => {
      dispatch(
        setIsIos({
          isIos: isIos(),
        })
      );

      dispatch(
        setIsSafari({
          isSafari: isSafari(),
        })
      );

      dispatch(
        setIsAndroid({
          isAndroid: isAndroid(),
        })
      );
    });
  }, [dispatch]);

  const showForm =
    shouldRenderForm ||
    (isTransitionAnimationEnabledForSubmission &&
      !hasSubmissionPageExitAnimationCompleted);

  const thankYouEventFiredRef = useRef(false);

  if (!showForm && !thankYouEventFiredRef.current) {
    const brandMatchesEventBody = toCustomerBrandMatchesEventBody({
      customerBrandMatches: matchedAndFilteredBrands,
    });

    // Use a ref to track if the thank you page event has been fired and fire it only once
    exposeEventToPartner({
      eventType: 'thankYouPageRequested',
      eventObject: {
        iFrameId,
        brandMatches: brandMatchesEventBody,
      },
    });

    thankYouEventFiredRef.current = true;
  }

  return (
    <>
      {showForm ? (
        <div className="formTransitionContainer" style={{ display: 'unset' }}>
          <form
            css={styling.form}
            onKeyDown={handleEnterPress}
            onClick={(eventData) => {
              exposeInitialWidgetInteractionEvent(eventData);
              if (isDynamicHeightEnabled) {
                const { failsafeAlgorithmData } =
                  window?.__private__?.dynamicWidgetHeightData || {};
                const { isDebugLoggingEnabled } =
                  window?.__private__?.dynamicWidgetHeightData || {};
                if (isDebugLoggingEnabled) {
                  console.log(
                    'DH: Updating failsafeAlgorithmData with this key-value pair',
                    { timeStampOfLastUserInteraction: Date.now() }
                  );
                }
                updateAndExposeDynamicHeightData({
                  failsafeAlgorithmData: {
                    ...failsafeAlgorithmData,
                    timeStampOfLastUserInteraction: Date.now(),
                  },
                });
              }
            }}
            {...overflowStyleProp}
          >
            <Grid
              container
              css={styling.gridContainer}
              direction="column"
              id="formContentContainer"
              data-testid="formContainer"
            >
              <Grid ref={headerRef} css={styling.header} item>
                <FormHeaderSection />
                <Progress />
              </Grid>

              <Page />

              {/* TODO: we should add a Footer component */}
              <Grid
                id="footerContainer"
                css={styling.grid}
                ref={footerRef}
                item
              >
                <div id="innerFooterContainer">
                  <Container maxWidth="md" disableGutters>
                    {isConsentStatementAboveNavigation ? (
                      <div
                        id="legalStatementContainer"
                        className="margin-top: 0.3rem"
                      >
                        <LegalStatement />
                        <NavigationSection />
                      </div>
                    ) : (
                      <NavigationSection />
                    )}
                  </Container>
                  <Container
                    maxWidth="md"
                    css={styling.privacyPolicyContainer}
                    disableGutters
                  >
                    {isCountdownTimerEnabled && (
                      <div css={styling.countdownTimerWrapper}>
                        <CountdownTimer />
                      </div>
                    )}
                    <div css={styling.privacyPolicyWrapper}>
                      <PrivacyPolicyLink />
                    </div>
                  </Container>
                  {!isConsentStatementAboveNavigation && (
                    <Container
                      id="legalStatementContainer"
                      css={styling.privacyPolicyContainer}
                      maxWidth="md"
                      disableGutters
                    >
                      <LegalStatement />
                    </Container>
                  )}
                </div>
              </Grid>

              <SuppliersListModal />
              {/* DynamicWidgetHeight requires SuppliersListModal to be
               *  decoupled from the LegalStatement and must live outside
               *  of the footerContainer for it to be vertically centred
               *  across the whole widget.
               */}
            </Grid>
            <BrowserAutoFiller />
          </form>
        </div>
      ) : (
        <ThankYouPage />
      )}
      <DynamicWidgetHeight />
      <LogRocketSession />
      <DomainWhitelistStatus />
    </>
  );
};

export default PageLayout;
